import { MenuPlannerItemsBulkResponse, apiCall } from 'api/menuPlanner/GetMenuPlannerItemsBulk';
import * as apiDelete from 'api/shoppingCart/DeleteShoppingCartIdV1';
import * as apiGetDetails from 'api/shoppingCart/GetShoppingCartGetDetailsIdV1';
import * as apiAction from 'api/shoppingCart/PostShoppingCartActionId';
import * as apiAddEntity from 'api/shoppingCart/PostShoppingCartAddToShoppingCartIdV1';
import * as apiPost from 'api/shoppingCart/PostShoppingCartV1';
import * as apiPut from 'api/shoppingCart/PutShoppingCartIdV1';
import * as apiPutAppearances from 'api/shoppingCart/PutShoppingCartUpdateAppearancesIdV1';
import * as apiGetAll from 'apiAuto/backend/paths/GetShoppingCartV1';
import { Data as InfoListData } from 'components/desktop/ShoppingCart/Detail/misc/InfoList';
import { Data as ReceiptData } from 'components/desktop/ShoppingCart/Detail/misc/Receipt';
import { Functor } from 'functions/Functor';
import { Optional } from 'functions/promiseExtensions';
import { StringList } from 'types/Product/Search/StringList';
import { AppearanceList } from 'types/ShoppingCart/AppearanceList';
import EntityType from 'types/ShoppingCart/EntityType';
import * as Person from 'types/ShoppingCart/Person';
import { ProductList } from 'types/ShoppingCart/ProductList';
import * as Status from 'types/ShoppingCart/Status';
import STATUS from 'types/ShoppingCart/Status';
import { ArrayItem } from 'types/_general/ArrayItem';
import * as Translation from 'types/_general/Translation';

export * as ShoppingCart from 'types/ShoppingCart/ShoppingCart';

export type Type = {
	id: string;
	name: Translation.Type<string>;
	description: Translation.Type<string>;
	status: STATUS;
	lastModified: Date;
	admin: Person.Type | null;
	price: number;
	default: boolean;
	appearances: AppearanceList.Type;
	products: ProductList.Type;
};

export function create(isDefault?: boolean): Type {
	return {
		id: '',
		name: {},
		description: {},
		status: STATUS.LIVE,
		lastModified: new Date(),
		admin: null,
		price: 0,
		default: isDefault ?? false,
		appearances: AppearanceList.create(),
		products: ProductList.create(),
	};
}

export async function deleteFromApi(id: string): Promise<void> {
	await apiDelete.callApi(id);
}

export async function setDefaultOnApi(id: string) {
	await apiAction.callApi(id, 'SetAsDefault');
}

export function getFullNameOfAdmin(obj: Type): string {
	return Optional.Maybe(obj.admin)
		.map((e) => e.firstName + ' ' + e.lastName)
		.getOrDefault('');
}

// TODO: remove function
function setEveryLanguageToSameValue(
	obj: Translation.Type<string>,
	input: string
): Translation.Type<string> {
	const output: Translation.Type<string> = {};
	for (const key of Object.keys(obj)) {
		output[key] = input;
	}
	output['en-US'] = input;
	return output;
}

function generateNewDefaultName(baseName: string, previousDefaultName?: string): string {
	let newDefaultName = baseName;

	if (previousDefaultName) {
		const match = previousDefaultName.match(/^Shopping Cart (\d+)$/);
		if (match) {
			const number = parseInt(match[1], 10);
			newDefaultName = `${baseName} ${number + 1}`;
		} else if (previousDefaultName === baseName) {
			newDefaultName = `${baseName} 1`;
		}
	}

	return newDefaultName;
}

export async function addNewDefaultCartAndSave(previousDefaultName?: string): Promise<Type> {
	const baseName = 'Shopping Cart';
	const newDefaultName = generateNewDefaultName(baseName, previousDefaultName);

	return await Functor.From(true)
		.map(create)
		.map((e) => setName(e, newDefaultName))
		.mapAsync(save)
		.get();
}

export async function save(obj: Type): Promise<Type> {
	if (obj.id === '') {
		return await postToApi(obj);
	}
	return Functor.From(obj)
		.mapAsync(putToApiAppearances)
		.mapAsync(putToApi)
		.mapAsync(enrichWithProductInformations)
		.get();
}

export async function putToApi(obj: Type): Promise<Type> {
	return (
		await Functor.From(obj)
			.map(mapToApi)
			.mapAsync((e) => apiPut.callApi(obj.id, e))
			.get()
	)
		.map((e) => mapFromApi(obj, e))
		.getOrDefault(obj);
}

export async function postToApi(obj: Type): Promise<Type> {
	return (await Functor.From(obj).map(mapToApi).mapAsync(apiPost.callApi).get())
		.map((e) => mapFromApi(obj, e))
		.getOrDefault(obj);
}

export function mapToApi(obj: Type): apiPost.Request & apiPut.Request {
	return {
		state: obj.status,
		isDefault: obj.default,
		nameTranslations: obj.name,
		descriptionTranslations: obj.description,
	};
}

export function mapFromApiGetAll(input: ArrayItem<apiGetAll.ResponseData>): Type {
	return {
		...create(),
		id: input.id,
		name: input.nameTranslations,
		description: input.descriptionTranslations,
		status: input.state as STATUS,
		lastModified: new Date(input.lastEditedDateUtc),
		admin: { firstName: input.creator ?? '', lastName: '' },
		default: input.isDefault,
		appearances: [],
		products: [],
	};
}

export async function moveToNextStatus(obj: Type): Promise<Type> {
	const status = Status.getNextStatus(obj.status);
	return await putToApi({
		...obj,
		status: status,
		default: false,
	});
}

export async function moveToPrevStatus(obj: Type): Promise<Type> {
	return await putToApi({
		...obj,
		status: Status.getPrevStatus(obj.status),
	});
}

export function mapFromApi(obj: Type, response: apiPost.ResponseData): Type {
	return {
		...obj,
		id: response.id,
	};
}

export function setName(obj: Type, name: string): Type {
	return { ...obj, name: setEveryLanguageToSameValue(obj.name, name) };
}

export function setProductQuantity(obj: Type, productId: string, quantity: number): Type {
	return { ...obj, products: ProductList.setQuantity(obj.products, productId, quantity) };
}

export function setDescription(obj: Type, description: string): Type {
	return { ...obj, description: setEveryLanguageToSameValue(obj.description, description) };
}

export async function setQuantityAndFetch(
	obj: Type,
	itemId: string,
	quantity: number
): Promise<Type> {
	return Functor.From(obj)
		.map((e) => setQuantity(e, itemId, quantity))
		.mapAsync(putToApiAppearances)
		.get();
}

export function setQuantity(obj: Type, itemId: string, quantity: number): Type {
	return {
		...obj,
		appearances: AppearanceList.setQuantity(obj.appearances, itemId, quantity),
	};
}

export function getIngredientsData(obj: Type, cultureCode: string): InfoListData[] {
	return Functor.From(obj.products)
		.map((e) => e.map((o) => o.ingredient.name))
		.map((e) => e.map((o) => Translation.getFirstTranslation(o, cultureCode)))
		.map(StringList.count)
		.map((e) => e.map((o) => ({ title: o.value, count: o.count })))
		.get();
}

export function getStoresData(obj: Type): InfoListData[] {
	return Functor.From(obj.products)
		.map((e) => e.map((o) => o.store))
		.map(StringList.count)
		.map((e) => e.map((o) => ({ title: o.value, count: o.count })))
		.get();
}

export function getReceiptData(obj: Type, cultureCode: string): ReceiptData[] {
	return Functor.From(obj.products)
		.map(ProductList.group)
		.map((e) => ProductList.mapToReceiptData(e, cultureCode))
		.get();
}

export function getProductsFromAppearanceId(obj: Type, id: string): ProductList.Type {
	return ProductList.getProductsFromAppearanceId(obj.products, id);
}

export function getTotalPrice(obj: Type, cultureCode: string): number {
	const receiptData = getReceiptData(obj, cultureCode);
	return receiptData.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

export function searchSources(
	obj: Type,
	searchTerm: string,
	cultureCode: string
): AppearanceList.Type {
	const output: AppearanceList.Type = [];
	for (const app of obj.appearances) {
		const products = getProductsFromAppearanceId(obj, app.id);
		const compareString: string = [
			Translation.getFirstTranslation(app.name, cultureCode),
			...products.map((e) => Translation.getFirstTranslation(e.ingredient.name, cultureCode)),
		]
			.join()
			.toLowerCase();
		if (!compareString.includes(searchTerm.toLowerCase())) continue;
		output.push(app);
	}
	return output;
}

export async function addEntityToCart(
	obj: Type,
	entity: {
		id: string;
		basketIds?: string[];
		type: EntityType;
		amount?: number;
	}
): Promise<AppearanceList.Type> {
	const response = await apiAddEntity.callApi(obj.id, {
		entityId: entity.id,
		entityType: entity.type,
		quantity: entity.amount ?? 1,
		erpRecipeIds: entity.basketIds ?? [],
	});
	return response
		.map((e) => e.appearances)
		.map((e) => AppearanceList.mapFromApi(obj.appearances, e))
		.getOrDefault([]);
}

export async function putToApiAppearances(obj: Type): Promise<Type> {
	const response = await apiPutAppearances.callApi(
		obj.id,
		AppearanceList.mapToApi(obj.appearances)
	);
	return response.map((e) => mapFromApiDetail(obj, e)).getOrDefault(obj);
}

export async function initialize(obj: Type): Promise<Type> {
	return await getDetailsFromApi(obj);
}

export async function getDetailsFromApi(obj: Type): Promise<Type> {
	return (await apiGetDetails.callApi(obj.id))
		.map((e) => mapFromApiDetail(obj, e))
		.getOrDefault(obj);
}

export function mapFromApiDetail(obj: Type, response: apiGetDetails.ResponseData): Type {
	return {
		...obj,
		appearances: AppearanceList.mapFromApi(obj.appearances, response.appearances),
		products: ProductList.mapFromApi(obj.products, response.productItems),
	};
}

export function getAmountOfEntity(obj: Type, entityType: EntityType): number {
	return AppearanceList.getAmountOfEntity(obj.appearances, entityType);
}

export async function enrichWithProductInformations(obj: Type): Promise<Type> {
	return {
		...obj,
		products: await ProductList.enrichWithProductInformations(obj.products),
	};
}

export async function removeAppearance(obj: Type, appearanceId: string): Promise<Type> {
	return {
		...obj,
		appearances: AppearanceList.removeAppearance(obj.appearances, appearanceId),
	};
}

export function extractIds<T extends { id: string }>(array: T[]): string[] {
	return array.map((item) => item.id);
}

export async function getMenuplanOrdersList(obj: Type): Promise<MenuPlannerItemsBulkResponse[]> {
	const items = obj.appearances.filter((source) => source.type == EntityType.MENUPLAN);
	if (items.length > 0) {
		const response = await apiCall(extractIds(items));
		let result: MenuPlannerItemsBulkResponse[] = [];
		response.do((x) => {
			result = x;
		});
		return result;
	}
	return [];
}
